import { useEffect, useMemo, useState } from "react";
import type { ChangeEvent } from "react";
import Swal from "sweetalert2";
import { apiRequest, getImageUrl } from "../../core/common/api";
import MetricCard from "../../shared/MetricCard";
import PaginationBar, { slicePage } from "../../shared/PaginationBar";
import ToggleSwitch from "../../shared/ToggleSwitch";
import AppImage from "../../shared/AppImage";
import ResetButton from "../../shared/ResetButton";
import useTableSearch from "../../shared/useTableSearch";

const EMPTY_FORM = {
  name: "",
  title: "",
  description: "",
  isActive: true,
};

const readImagePreview = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Image preview failed"));
    reader.readAsDataURL(file);
  });

const Banners = () => {
  const [banners, setBanners] = useState<any[]>([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState("");
  const [editingId, setEditingId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState("");
  const searchTerm = useTableSearch(search);
  const [isActiveFilter, setIsActiveFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const loadBanners = async () => {
    setLoading(true);

    try {
      const result = await apiRequest("/banners", "GET");
      setBanners(result.banners || []);
    } catch (error: any) {
      Swal.fire("Error", error.message || "Unable to load banners", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBanners();
  }, []);

  const filteredBanners = useMemo(() => {
    const query = searchTerm.toLowerCase();

    return banners.filter((banner) => {
      const searchableText = `
        ${banner.name || ""}
        ${banner.title || ""}
        ${banner.description || ""}
      `.toLowerCase();

      const searchMatch = !query || searchableText.includes(query);
      const isActiveMatch =
        isActiveFilter === "all" ||
        (isActiveFilter === "true" && banner.isActive) ||
        (isActiveFilter === "false" && !banner.isActive);

      return searchMatch && isActiveMatch;
    });
  }, [banners, searchTerm, isActiveFilter]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, isActiveFilter]);

  const paged = slicePage(filteredBanners, page, limit);

  const handleChange = (event: any) => {
    const { name, value, type, checked } = event.target;

    setForm((oldForm) => ({
      ...oldForm,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleImageUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      Swal.fire("Invalid Image", "Please select a valid image.", "error");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      Swal.fire(
        "Image Too Large",
        "Image size must be less than 5MB.",
        "error",
      );
      return;
    }

    try {
      const preview = await readImagePreview(file);
      setImageFile(file);
      setImagePreview(preview);
    } catch (error: any) {
      Swal.fire("Error", error.message || "Image preview failed", "error");
    }
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setImageFile(null);
    setImagePreview("");
    setEditingId("");
  };

  const validateForm = () => {
    if (!form.name.trim()) return "Banner name is required.";
    if (!form.title.trim()) return "Banner title is required.";
    if (!editingId && !imageFile) return "Banner image is required.";
    if (form.name.trim().length > 50)
      return "Banner name cannot exceed 50 characters.";
    if (form.title.trim().length > 100)
      return "Banner title cannot exceed 100 characters.";
    if (form.description.trim().length > 1000)
      return "Description cannot exceed 1000 characters.";
    return "";
  };

  const buildPayload = () => {
    const payload = new FormData();
    payload.append("name", form.name.trim());
    payload.append("title", form.title.trim());
    payload.append("description", form.description.trim());
    payload.append("isActive", String(form.isActive));

    if (imageFile) {
      payload.append("image", imageFile);
    }

    return payload;
  };

  const handleSubmit = async (event: any) => {
    event.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      Swal.fire("Validation Error", validationError, "error");
      return;
    }

    setSaving(true);

    try {
      const endpoint = editingId ? `/banners/${editingId}` : "/banners";
      const method = editingId ? "PUT" : "POST";
      const result = await apiRequest(endpoint, method, buildPayload());

      if (!result.success) {
        throw new Error(result.message || "Banner save failed");
      }

      await Swal.fire({
        title: editingId ? "Banner Updated" : "Banner Added",
        icon: "success",
        timer: 1400,
        showConfirmButton: false,
      });

      resetForm();
      loadBanners();
    } catch (error: any) {
      Swal.fire("Save Failed", error.message || "Banner save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const editBanner = (banner: any) => {
    setForm({
      name: banner.name || "",
      title: banner.title || "",
      description: banner.description || "",
      isActive: Boolean(banner.isActive),
    });
    setImageFile(null);
    setImagePreview(banner.image || "");
    setEditingId(banner._id);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const deleteBanner = async (id: string) => {
    const confirmation = await Swal.fire({
      title: "Delete banner?",
      text: "This banner will be removed permanently.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Delete",
    });

    if (!confirmation.isConfirmed) return;

    try {
      const result = await apiRequest(`/banners/${id}`, "DELETE");

      if (!result.success) {
        throw new Error(result.message || "Banner delete failed");
      }

      if (editingId === id) {
        resetForm();
      }

      await loadBanners();
      Swal.fire({
        title: "Banner Deleted",
        icon: "success",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (error: any) {
      Swal.fire(
        "Delete Failed",
        error.message || "Banner delete failed",
        "error",
      );
    }
  };

  const metrics = [
    {
      label: "Total Banners",
      value: 0,
      icon: "🗂️",
      tone: "orange",
    },
    {
      label: "Active Banners",
      value: 0,
      icon: "✅",
      tone: "blue",
    },
    {
      label: "Inactive Banners",
      value: 0,
      icon: "⏸️",
      tone: "purple",
    },
  ];

  return (
    <>
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">MANAGEMENT</div>

          <h1 className="page-title">
            All <em>Banners</em>
          </h1>

          <div className="page-sub">Manage banners and their details.</div>
        </div>

        <div className="page-hdr-actions">
          <button type="button" className="btn-sm btn-primary" onClick={resetForm}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 5v14" />
              <path d="M5 12h14" />
            </svg>
            Add Banner
          </button>
        </div>
      </div>

      <section className="metrics-grid">
        {metrics.map((item) => (
          <MetricCard key={item.label} metric={item} />
        ))}
      </section>

      <form className="card banner-page" onSubmit={handleSubmit} noValidate>
        <div className="card-header">
          <div>
            <h2>{editingId ? "✏️ Edit Banner" : "Add Banner"}</h2>
            <p>Fields marked with * are required.</p>
          </div>

          {editingId && <strong>Editing</strong>}
        </div>

        <div className="card-body">
          <div className="three-col">
            <div className="banner-field">
              <label>Banner Name *</label>

              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                maxLength={50}
                placeholder="Summer Hero Banner"
              />

              <small>{form.name.length}/50</small>
            </div>
            <div className="banner-field">
              <label>Banner Title *</label>

              <input
                name="title"
                value={form.title}
                onChange={handleChange}
                maxLength={100}
                placeholder="Big Summer Sale Is Live"
              />

              <small>{form.title.length}/100</small>
            </div>
         
            <div className="banner-field">
              <label>Status</label>
              <ToggleSwitch
                checked={form.isActive}
                onChange={(isActive) => setForm((current) => ({ ...current, isActive }))}
                checkedLabel="Active"
                uncheckedLabel="Inactive"
              />
            </div>
         </div>

          <div className="two-col">
            <div className="banner-field">
              <label>Description</label>

              <textarea
                id="banner-description"
                name="description"
                value={form.description}
                onChange={handleChange}
                maxLength={1000}
                rows={5}
                placeholder="Write complete banner description..."
              />

              <small>{form.description.length}/1000</small>
            </div>
            <ImageUpload
              label="Banner Image"
              preview={imagePreview}
              onChange={handleImageUpload}
            />
          </div>
        </div>

        <div className="card-footer">
          <button
            type="button"
            className="btns btn-light"
            onClick={resetForm}
            disabled={saving}
          >
            ✖ Clear
          </button>

          <button type="submit" className="btns btn-primary" disabled={saving}>
            💾 {saving ? "Saving..." : editingId ? "Update" : "Save"}
          </button>
        </div>
      </form>

      <div className="toolbar">
        <div className="toolbar-search">
          <svg viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            value={search}
            placeholder="Search name, title"
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <select
          className="filter-select"
          value={isActiveFilter}
          onChange={(event) => setIsActiveFilter(event.target.value)}
        >
          <option value="all">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
        <ResetButton
          onClick={() => {
            setSearch("");
            setIsActiveFilter("all");
          }}
        />
      </div>

      <section className="table-card">
        <div className="table-head">
          <h2>Banners List</h2>
          <p>Manage Banners</p>
        </div>

        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Banner</th>
                <th>Title</th>
                <th>Description</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="empty-state">
                    Loading banners...
                  </td>
                </tr>
              ) : filteredBanners.length === 0 ? (
                <tr>
                  <td colSpan={5} className="empty-state">
                    No data found.
                  </td>
                </tr>
              ) : (
                paged.rows.map((item) => {
                  return (
                    <tr key={item._id}>
                      <td>
                        <div className="img-name-cell">
                          <AppImage
                            src={getImageUrl(item.image)}
                            alt={item.name}
                            crossOrigin="anonymous"
                          />

                          <div>
                            <strong>{item.name}</strong>
                          </div>
                        </div>
                      </td>

                      <td>{item.title}</td>
                      <td>{item.description || "No description"}</td>

                      <td>
                        <span
                          className={`status-badge ${
                            item.isActive === true ? "success" : "danger"
                          }`}
                        >
                          {item.isActive === true ? "Active" : "Inactive"}
                        </span>
                      </td>

                      <td>
                        <div className="actions-btns">
                          <button
                            type="button"
                            title="Edit"
                            className="action-button edit"
                            onClick={() => editBanner(item)}
                          >
                            ✏️
                          </button>

                          <button
                            type="button"
                            title="Delete"
                            className="action-button delete"
                            onClick={() => deleteBanner(item._id)}
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <PaginationBar
          page={paged.page}
          limit={limit}
          total={paged.total}
          totalPages={paged.totalPages}
          itemLabel="banners"
          onPageChange={setPage}
          onLimitChange={(nextLimit) => {
            setPage(1);
            setLimit(nextLimit);
          }}
        />
      </section>
    </>
  );
};

const ImageUpload = ({
  label,
  preview,
  onChange,
}: {
  label: string;
  preview: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}) => (
  <label className="banner-image-field">
    <span>{label}</span>

    <div className="banner-image-upload">
      {preview ? (
        <AppImage
          src={preview}
          alt={`${label} preview`}
          crossOrigin={preview.startsWith("http") ? "anonymous" : undefined}
        />
      ) : (
        <div className="upload-placeholder">
          <strong>☁️</strong>
          <b>Upload image</b>
          <small>PNG, JPG or WEBP up to 5MB</small>
        </div>
      )}

      <input
        hidden
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={onChange}
      />
    </div>
  </label>
);

export default Banners;
